import { Channel } from 'lib-shared-types-channels';
import { useMemo } from 'react';

import { CopilotAction } from 'components/copilotDropdown';
import {
  EditorCopilotDropdownFeedback,
  EditorCopilotDropdownImproveReply,
  EditorCopilotDropdownMatchToStyleGuide,
  EditorCopilotDropdownSpellingAndGrammar,
  EditorCopilotDropdownTranslate,
} from 'components/editorCopilotDropdown/components';
import { copilotUtils } from 'utils/copilot';

import { EditorCopilotDropdownExpandText } from '../components/expandText/copilotDropdownExpandText';
import { DEFAULT_PINNED_STATE } from '../editorCopilotDropdown';

export interface ModalCopilotActions {
  editorCopilotActions: Record<string, CopilotAction>;
  internalNoteEditorCopilotActions: Record<string, CopilotAction>;
}

export const useGetComposeModalCopilotActions = (
  channel: Channel,
  isDisabled: () => boolean | string,
): ModalCopilotActions => {
  const editorCopilotActions = useMemo(() => {
    return {
      improveReply: {
        title: 'Improve message' as string,
        icon: 'pen',
        commandName: 'richTextEditor:improveReply',
        content: (
          <EditorCopilotDropdownImproveReply
            channel={channel}
            lastMessage=""
            hasStyleGuideForChannel={!isDisabled()}
            disabledImprovements={[
              {
                value: 'match_tone',
                tooltip: 'Not available when composing a new message',
              },
            ]}
          />
        ),
        isPinned: ({ pinnedState }) =>
          pinnedState?.improveReply ?? DEFAULT_PINNED_STATE.improveReply,
      },
      improveReplyMatchStyleGuide: {
        title: 'Match to style guide',
        icon: 'widget-customize',
        commandName: 'richTextEditor:matchToStyleGuide',
        content: (
          <EditorCopilotDropdownMatchToStyleGuide
            channel={channel}
            lastMessage=""
          />
        ),
        isDisabled,
        isPinned: ({ pinnedState }) =>
          pinnedState?.improveReplyMatchStyleGuide ??
          DEFAULT_PINNED_STATE.improveReplyMatchStyleGuide,
      },
      translate: {
        title: 'Translate',
        icon: 'translate',
        commandName: 'richTextEditor:translate',
        content: <EditorCopilotDropdownTranslate />,
      },
      checkSpellingAndGrammar: {
        title: 'Fix spelling and grammar',
        commandName: 'richTextEditor:checkSpellingAndGrammar',
        icon: 'grammar-spelling',
        content: <EditorCopilotDropdownSpellingAndGrammar />,
        isPinned: ({ pinnedState }) =>
          pinnedState?.checkSpellingAndGrammar ??
          DEFAULT_PINNED_STATE.checkSpellingAndGrammar,
        isDisabled: ({ currentEditorText }) => {
          const { isDisabled, tooltipContent } =
            copilotUtils.spellingAndGrammar.getStatus({
              text: currentEditorText || '',
            });

          if (isDisabled) {
            return tooltipContent;
          }
          return false;
        },
      },
      expandText: {
        title: 'Expand message',
        commandName: 'richTextEditor:expandText',
        icon: 'chat-plus',
        content: (
          <EditorCopilotDropdownExpandText channel={channel} lastMessage="" />
        ),
        isPinned: ({ pinnedState }) =>
          pinnedState?.expandText ?? DEFAULT_PINNED_STATE.expandText,
        isDisabled: ({ currentEditorText }) => {
          const { isDisabled, tooltipContent } =
            copilotUtils.expandText.getStatus({
              text: currentEditorText || '',
            });

          if (isDisabled) {
            return tooltipContent;
          }

          return false;
        },
      },
      sendFeedback: {
        title: 'Send feedback',
        icon: 'paper-plane',
        hasDividerBefore: true,
        content: <EditorCopilotDropdownFeedback />,
      },
    } satisfies Record<string, CopilotAction>;
  }, [channel, isDisabled]);

  const internalNoteEditorCopilotActions = useMemo(() => {
    const { improveReplyMatchStyleGuide, ...rest } = editorCopilotActions;

    const internalNoteCopilotActions = {
      ...rest,
    };

    internalNoteCopilotActions.improveReply = {
      ...editorCopilotActions.improveReply,
      title: 'Improve note',
      content: (
        <EditorCopilotDropdownImproveReply
          channel={channel}
          lastMessage=""
          disabledImprovements={[
            {
              value: 'match_tone',
              tooltip: 'Not available for internal notes',
            },
          ]}
        />
      ),
    };

    return internalNoteCopilotActions;
  }, [channel, editorCopilotActions]);

  return { editorCopilotActions, internalNoteEditorCopilotActions };
};
